import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createAccessory, updateAccessoryCatalogue } from '@/services/accessories';
import { fetchSuppliers } from '@/services/suppliers';
import { queryKeys } from '@/lib/queryKeys';
import { parseDatabaseError } from '@/lib/errors';
import { Accessory } from '@/types/models';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { useAuth } from '@/context/AuthContext';
import { Save, Loader2 } from 'lucide-react';

const accessorySchema = z.object({
  name: z.string().min(2, 'Accessory name must be at least 2 characters').max(150),
  sku: z.string().max(100).optional().or(z.literal('')),
  category: z.string().min(1, 'Category is required').max(100),
  initial_quantity: z.coerce.number().min(0, 'Quantity cannot be negative').optional(),
  reorder_level: z.coerce.number().min(0, 'Reorder level cannot be negative'),
  selling_price: z.coerce.number().min(1, 'Selling price is required'),
  average_cost: z.union([z.literal(''), z.coerce.number().min(0)]).optional(),
  supplier_id: z.string().optional().or(z.literal('')),
  notes: z.string().max(1000).optional().or(z.literal('')),
  is_public: z.boolean().default(true),
  is_active: z.boolean().default(true),
});

type AccessoryFormData = z.infer<typeof accessorySchema>;

interface AccessoryFormProps {
  accessory?: Accessory;
  onSuccess: () => void;
  onCancel: () => void;
}

export const AccessoryForm: React.FC<AccessoryFormProps> = ({
  accessory,
  onSuccess,
  onCancel,
}) => {
  const { hasPermission } = useAuth();
  const canViewCosts = hasPermission('purchases');
  const queryClient = useQueryClient();
  const isEdit = !!accessory;
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { data: suppliersData } = useQuery({
    queryKey: queryKeys.suppliers.list({ page: 0 }),
    queryFn: () => fetchSuppliers({ page: 0 }),
    enabled: canViewCosts,
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<AccessoryFormData>({
    resolver: zodResolver(accessorySchema),
    defaultValues: {
      name: accessory?.name ?? '',
      sku: accessory?.sku ?? '',
      category: accessory?.category ?? 'Charger & Power',
      initial_quantity: accessory?.quantity ?? 0,
      reorder_level: accessory?.reorder_level ?? 5,
      selling_price: accessory?.selling_price ?? 0,
      average_cost: accessory?.average_cost ?? '',
      supplier_id: accessory?.supplier_id ?? '',
      notes: accessory?.notes ?? '',
      is_public: accessory?.is_public ?? true,
      is_active: accessory?.is_active ?? true,
    },
  });

  const mutation = useMutation({
    mutationFn: (data: AccessoryFormData) => {
      if (isEdit) {
        const payload = {
          name: data.name,
          sku: data.sku?.trim() || null,
          category: data.category,
          reorder_level: data.reorder_level,
          selling_price: data.selling_price,
          average_cost: canViewCosts && typeof data.average_cost === 'number' ? data.average_cost : undefined,
          supplier_id: data.supplier_id || null,
          notes: data.notes || null,
          is_public: data.is_public,
          is_active: data.is_active,
        };
        return updateAccessoryCatalogue(accessory.id, payload);
      } else {
        const payload = {
          name: data.name,
          sku: data.sku?.trim() || undefined,
          category: data.category,
          quantity: data.initial_quantity ?? 0,
          reorder_level: data.reorder_level,
          selling_price: data.selling_price,
          average_cost: canViewCosts && typeof data.average_cost === 'number' ? data.average_cost : undefined,
          supplier_id: data.supplier_id || null,
          notes: data.notes || null,
          is_public: data.is_public,
          is_active: data.is_active,
        };
        return createAccessory(payload);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.accessories.all });
      onSuccess();
    },
    onError: (err: unknown) => {
      setSubmitError(parseDatabaseError(err));
    },
  });

  const onSubmit = (data: AccessoryFormData) => {
    setSubmitError(null);
    mutation.mutate(data);
  };

  const inputClass = (hasError: boolean) =>
    `w-full px-3 py-2 bg-brand-bg border rounded-md text-sm text-brand-black placeholder-brand-muted focus:outline-none focus:ring-2 focus:ring-brand-green focus:border-transparent transition-all ${
      hasError ? 'border-brand-red focus:ring-brand-red' : 'border-brand-border'
    }`;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 max-h-[75vh] overflow-y-auto pr-1" noValidate>
      {submitError && <ErrorAlert message={submitError} />}

      {/* Name */}
      <div>
        <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider mb-1">
          Accessory Name <span className="text-brand-red">*</span>
        </label>
        <input {...register('name')} placeholder="e.g. Dell 65W USB-C Charger Original" className={inputClass(!!errors.name)} />
        {errors.name && <p className="mt-1 text-xs text-brand-red">{errors.name.message}</p>}
      </div>

      {/* SKU & Category */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider mb-1">
            SKU / Item Code {isEdit ? '' : <span className="text-brand-muted font-normal text-[10px] lowercase">(optional - auto-generated)</span>}
          </label>
          <input {...register('sku')} placeholder={isEdit ? 'ACC-CHG-65W' : 'Leave blank to auto-generate'} className={inputClass(!!errors.sku)} />
        </div>
        <div>
          <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider mb-1">
            Category <span className="text-brand-red">*</span>
          </label>
          <select {...register('category')} className={inputClass(!!errors.category)}>
            <option value="Charger & Power">Charger & Power</option>
            <option value="RAM & Memory">RAM & Memory</option>
            <option value="SSD & Storage">SSD & Storage</option>
            <option value="Bag & Sleeve">Bag & Sleeve</option>
            <option value="Mouse & Keyboard">Mouse & Keyboard</option>
            <option value="Cable & Adapter">Cable & Adapter</option>
            <option value="Screen & Spare Parts">Screen & Spare Parts</option>
            <option value="Other Accessories">Other Accessories</option>
          </select>
        </div>
      </div>

      {/* Quantity & Reorder Level */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {!isEdit ? (
          <div>
            <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider mb-1">
              Initial Quantity
            </label>
            <input {...register('initial_quantity')} type="number" placeholder="10" className={inputClass(!!errors.initial_quantity)} />
          </div>
        ) : (
          <div>
            <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider mb-1">
              Stock Quantity
            </label>
            <div className="px-3 py-2 bg-brand-bg border border-brand-border rounded-md text-sm text-brand-muted font-medium flex justify-between items-center">
              <span>{accessory.quantity} in stock</span>
              <span className="text-[10px] text-brand-green font-semibold">Use Adjust Stock button</span>
            </div>
          </div>
        )}
        <div>
          <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider mb-1">
            Reorder Alert Threshold <span className="text-brand-red">*</span>
          </label>
          <input {...register('reorder_level')} type="number" placeholder="5" className={inputClass(!!errors.reorder_level)} />
        </div>
      </div>

      {/* Prices */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-brand-bg p-3 rounded-lg border border-brand-border">
        <div>
          <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider mb-1">
            Selling Price (PKR) <span className="text-brand-red">*</span>
          </label>
          <input {...register('selling_price')} type="number" placeholder="4500" className={inputClass(!!errors.selling_price)} />
          {errors.selling_price && <p className="mt-1 text-xs text-brand-red">{errors.selling_price.message}</p>}
        </div>
        {canViewCosts && (
          <div>
            <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider mb-1">
              Average Cost Price (PKR)
            </label>
            <input {...register('average_cost')} type="number" placeholder="3200" className={inputClass(!!errors.average_cost)} />
          </div>
        )}
      </div>

      {/* Supplier */}
      {canViewCosts && suppliersData && (
        <div>
          <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider mb-1">
            Supplier
          </label>
          <select {...register('supplier_id')} className={inputClass(!!errors.supplier_id)}>
            <option value="">Select Supplier (Optional)</option>
            {suppliersData.data.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Notes */}
      <div>
        <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider mb-1">
          Notes
        </label>
        <textarea {...register('notes')} rows={2} placeholder="Item specs, compatibility, etc." className={`${inputClass(!!errors.notes)} resize-none`} />
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end space-x-3 pt-3 border-t border-brand-border">
        <button type="button" onClick={onCancel} disabled={mutation.isPending} className="px-4 py-2 text-sm font-medium text-brand-muted hover:text-brand-black transition-colors disabled:opacity-50">
          Cancel
        </button>
        <button
          type="submit"
          disabled={mutation.isPending || (isEdit && !isDirty)}
          className="inline-flex items-center space-x-2 px-4 py-2 bg-brand-green text-white text-sm font-semibold rounded-md hover:bg-brand-green-hover transition-colors disabled:opacity-50 shadow-sm"
        >
          {mutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          <span>{mutation.isPending ? 'Saving...' : isEdit ? 'Update Catalogue Details' : 'Add Catalogue Item'}</span>
        </button>
      </div>
    </form>
  );
};
